import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetBrands } from "../../hooks/brands/useGetBrands";
import { useDeleteBrand } from "../../hooks/brands/useDeleteBrand";
import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import BrandsTableRow from "./Table/BrandsTableRow";
import { BrandsTableColumns } from "./Table/BrandsTableColumns";
import { usePermissions } from "../../auth/PermissionsContext";

const BrandsTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("brands_list"),
            create: has("brands_create"),
            edit: has("brands_edit"),
            delete: has("brands_delete"),
        }),
        [has]
    );

    const navigate = useNavigate();

    // Filter States
    const [statusFilter, setStatusFilter] = useState("");

    // CRUD Hooks
    const { deleteBrandMutate, isDelete } = useDeleteBrand();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/brands",
        deleteSingle: deleteBrandMutate,
    });

    const params = useMemo(
        () => ({
            page,
            per_page: perPage,
            search: search || undefined,
            is_active: statusFilter !== "" ? statusFilter : undefined,
            sort_by: 'created_at',
            sort_order: order,
        }),
        [page, perPage, search, statusFilter, order]
    );

    const { isLoading, error, brands, meta } = useGetBrands(params);

    // تجهيز الفلاتر لـ TableLayout
    const tableFilters = [
        {
            name: "status",
            label: { ar: "الحالة", en: "Status" },
            value: statusFilter,
            onChange: (val) => setStatusFilter(val),
            options: [
                { value: "", label: { ar: "الكل", en: "All" } },
                { value: "1", label: { ar: "نشط", en: "Active" } },
                { value: "0", label: { ar: "غير نشط", en: "Inactive" } },
            ]
        }
    ];

    const renderRow = (rowParams) => (
        <BrandsTableRow
            key={rowParams.item.id}
            brand={rowParams.item}
            index={rowParams.index}
            isSelected={selectedIds.includes(rowParams.item.id)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
        />
    );

    const title = isArabic ? "العلامات التجارية" : "Brands";
    const subtitle = isArabic ? "إدارة العلامات التجارية للشركات" : "Manage company brands";

    return (
        <TableLayout
            title={title}
            subtitle={subtitle}
            search={search}
            onSearchChange={setSearch}
            addButtonLabel={isArabic ? "إضافة علامة" : "Add Brand"}
            onAddClick={() => navigate("/brands/create")}
            addButtonVisible={perms.create}
            isLoading={isLoading}
            error={error}
            items={brands}
            meta={meta}
            page={page}
            perPage={perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            order={order}
            onRequestSort={handleRequestSort}
            columns={BrandsTableColumns}
            renderRow={renderRow}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            menuDisableEdit={!perms.edit}
            menuDisableDelete={!perms.delete}
            isDeletingSingle={isDelete}
            selectedIds={selectedIds}
            onSelectAllClick={(e) => handleSelectAllClick(e, brands)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onBulkDelete={handleBulkDelete}
            bulkDeleteDisabled={!perms.delete}
            filters={tableFilters}
            onClearAllFilters={() => {
                setStatusFilter("");
            }}
            emptyLabelAr="لا توجد علامات تجارية"
            emptyLabelEn="No brands found"
        />
    );
};

export default BrandsTable;
